/**
 * RidesAvailabilityService
 * Driver listings (availability): create per event, mine, update, arrived / cancel / close, bookings.
 * Route: rides/api/rides/availability
 */
import { BaseService } from '../base/BaseService';
import { unwrapRidesResponse as unwrap } from './unwrapRidesResponse';
/** Driver availability service (JWT; driver must be fully verified to list). */
export class RidesAvailabilityService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? RidesAvailabilityService.DEFAULT_SERVICE_PATH,
            basePath: RidesAvailabilityService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    /** POST api/rides/availability — one listing per event in `eventIDs`. */
    async create(request) {
        return (await this.createRaw(request)).data ?? [];
    }
    createRaw(request) {
        return this.http.post(this.buildUrl(), request);
    }
    /** GET api/rides/availability/mine — the caller's listings (with bookings). */
    async getMine() {
        return (await this.getMineRaw()).data ?? [];
    }
    getMineRaw() {
        return this.http.get(this.buildUrl('mine'));
    }
    /** GET api/rides/availability/{id} */
    async getById(id) {
        return unwrap(await this.getByIdRaw(id), `Ride listing ${id} not found`);
    }
    getByIdRaw(id) {
        return this.http.get(this.buildUrl(String(id)));
    }
    /** PUT api/rides/availability/{id} — seats / departure / service time / notes. */
    async update(id, request) {
        return unwrap(await this.updateRaw(id, request), `Failed to update ride listing ${id}`);
    }
    updateRaw(id, request) {
        return this.http.put(this.buildUrl(String(id)), request);
    }
    /** POST api/rides/availability/{id}/arrived — "I've arrived" (notifies riders). */
    async markArrived(id) {
        return unwrap(await this.markArrivedRaw(id), `Failed to mark ride listing ${id} as arrived`);
    }
    markArrivedRaw(id) {
        return this.http.post(this.buildUrl(`${id}/arrived`), {});
    }
    /** POST api/rides/availability/{id}/cancel — cancels the listing and its bookings (notifies riders). */
    async cancel(id, request = {}) {
        return unwrap(await this.cancelRaw(id, request), `Failed to cancel ride listing ${id}`);
    }
    cancelRaw(id, request = {}) {
        return this.http.post(this.buildUrl(`${id}/cancel`), request);
    }
    /** POST api/rides/availability/{id}/close — stop accepting bookings (existing bookings stay). */
    async close(id) {
        return unwrap(await this.closeRaw(id), `Failed to close ride listing ${id}`);
    }
    closeRaw(id) {
        return this.http.post(this.buildUrl(`${id}/close`), {});
    }
    /** GET api/rides/availability/{id}/bookings — riders on this listing (names, party, forMinor; no phones). */
    async getBookings(id) {
        return (await this.getBookingsRaw(id)).data ?? [];
    }
    getBookingsRaw(id) {
        return this.http.get(this.buildUrl(`${id}/bookings`));
    }
}
RidesAvailabilityService.DEFAULT_SERVICE_PATH = 'rides';
RidesAvailabilityService.BASE_PATH = 'api/rides/availability';
//# sourceMappingURL=RidesAvailabilityService.js.map